import React from 'react';
import { Box, IconButton, Stack, Tooltip, Paper } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { API_BASE_URL } from '../../services/api';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';

interface Props {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  bulletImageUrl?: string; // niestandardowy punktator listy
}

const RichTextEditor: React.FC<Props> = ({ value, onChange, minHeight = 160, bulletImageUrl }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);

  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    ref.current && onChange(ref.current.innerHTML);
  };

  React.useEffect(() => {
    // Aktualizuj tylko gdy zewnętrzna wartość zmieni się i różni od bieżącej
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    ref.current && onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt('Wprowadź URL');
    if (url) exec('createLink', url);
  };

  const clear = () => {
    exec('removeFormat');
  };

  // Wyznacz host backendu dla /uploads (działa lokalnie i prod)
  const uploadsHost = (API_BASE_URL || '').replace(/\/?api$/, '');
  const resolvedBulletUrl = bulletImageUrl || `${uploadsHost}/api/uploads/recruit/check_bullet.png`;
  const checkSvgEncoded = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DE9364" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l2.5 2.5L16 9"/></svg>');
  const dataUrl = `url("data:image/svg+xml,${checkSvgEncoded}")`;

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
        <Tooltip title="Pogrubienie"><span><IconButton size="small" onClick={() => exec('bold')}><FormatBoldIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Kursywa"><span><IconButton size="small" onClick={() => exec('italic')}><FormatItalicIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Podkreślenie"><span><IconButton size="small" onClick={() => exec('underline')}><FormatUnderlinedIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Lista punktowana"><span><IconButton size="small" onClick={() => exec('insertUnorderedList')}><FormatListBulletedIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Lista numerowana"><span><IconButton size="small" onClick={() => exec('insertOrderedList')}><FormatListNumberedIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Lista (check)"><span><IconButton size="small" onClick={() => exec('insertUnorderedList')}><CheckCircleOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Link"><span><IconButton size="small" onClick={addLink}><LinkIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Cofnij"><span><IconButton size="small" onClick={() => exec('undo')}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Ponów"><span><IconButton size="small" onClick={() => exec('redo')}><RedoIcon fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Wyczyść formatowanie"><span><IconButton size="small" onClick={clear}><FormatClearIcon fontSize="small" /></IconButton></span></Tooltip>
      </Stack>
      <Box
        ref={ref}
        contentEditable
        onInput={handleInput}
        sx={{
          minHeight,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 1,
          outline: 'none',
          '&:focus': { borderColor: '#90caf9', boxShadow: '0 0 0 2px rgba(144,202,249,0.3)' },
          // niestandardowe punktorowanie list
          '& ul': {
            listStyle: 'none',
            paddingLeft: '26px'
          },
          '& ul li': {
            position: 'relative'
          },
          '& ul li::before': {
            content: '""',
            position: 'absolute',
            left: '-22px',
            top: '3px',
            width: '16px',
            height: '16px',
            display: 'inline-block',
            verticalAlign: 'middle',
            backgroundImage: `${dataUrl}`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '16px 16px'
          }
        }}
        suppressContentEditableWarning
      />
    </Paper>
  );
};

export default RichTextEditor;


